<template>
  <div
    class="kiosk-home-v2 fade-in"
    :style="pageStyle"
    @click="goToOrderType()"
  >
    <div
      v-if="store"
      class="kiosk-home-content"
    >
      <!-- Logo section (hidden when kiosk_disable_logo_text is true) -->
      <img
        v-show="!hideLogoAndText"
        :src="logoSource"
        class="kiosk-logo-v2"
        alt="Logo"
      >

      <!-- Welcome section (hidden when kiosk_disable_logo_text is true) -->
      <div
        v-show="!hideLogoAndText"
        class="kiosk-welcome-section"
      >
        <h1 class="kiosk-title">
          Welcome
        </h1>
        <h3 class="kiosk-subtitle">
          Touch to begin your order
        </h3>
      </div>

      <!-- Footer section -->
      <div class="kiosk-footer-v2">
        <div
          v-if="terminal"
          class="terminal-version-v2"
        >
          {{ terminal.name }}: {{ terminal.id }}
        </div>
        <img
          v-long-press="5000"
          src="@/assets/images/powered.svg"
          class="kiosk-powered-v2"
          @long-press-start="goToTools"
          alt="Powered by"
        >
      </div>
    </div>
    <scale-loader
      v-if="!store"
      :color="spinner.color"
      :size="spinner.size"
    />
  </div>
</template>

<script>
import LongPress from "vue-directive-long-press";

export default {
  name: "KioskHomeV2",
  components: {
    ScaleLoader: () => import("vue-spinner/src/ScaleLoader.vue"),
  },
  directives: {
    "long-press": LongPress,
  },
  data() {
    return {
      spinner: {
        size: "50px",
        color: "#2563EB",
      },
    };
  },
  computed: {
    terminal() {
      return this.$store.getters["config/terminal"];
    },
    store() {
      return this.$store.getters["config/store"];
    },
    isWhitelabelEnabled() {
      return this.$store.getters["config/isWhitelabelEnabled"];
    },
    kioskDisableLogoText() {
      return this.$store.getters["config/kioskDisableLogoText"];
    },
    isKioskMode() {
      return this.$store.getters["config/isKioskMode"];
    },
    hideLogoAndText() {
      return this.isKioskMode && this.kioskDisableLogoText;
    },
    primaryColor() {
      return this.$store.getters["config/primaryColor"];
    },
    whitelabelLogo() {
      return this.$store.getters["config/whitelabelLogo"];
    },
    logoSource() {
      return this.isWhitelabelEnabled && this.whitelabelLogo
        ? this.whitelabelLogo
        : this.store?.logo || require("@/assets/images/logo.svg");
    },
    pageStyle() {
      const styles = {};
      const useOverlay = !this.hideLogoAndText;

      if (this.store?.background) {
        if (useOverlay) {
          styles.backgroundImage = `linear-gradient(rgba(15, 23, 42, 0.5), rgba(15, 23, 42, 0.8)), url(${this.store.background})`;
        } else {
          styles.backgroundImage = `url(${this.store.background})`;
        }
        styles.backgroundSize = "cover";
        styles.backgroundPosition = "center";
      } else if (this.isWhitelabelEnabled && this.primaryColor) {
        styles.background = `linear-gradient(180deg, ${this.primaryColor} 0%, ${this.primaryColor} 100%)`;
      }

      return styles;
    },
    activeDiningOptions() {
      if (!this.terminal || !this.terminal.dining_options) return [];
      return this.terminal.dining_options.filter((option) => option.active);
    },
  },
  methods: {
    async goToOrderType() {
      if (
        this.terminal &&
        this.activeDiningOptions &&
        this.activeDiningOptions.length === 1
      ) {
        const diningOption = this.activeDiningOptions[0];
        const newOrder = await this.$store.dispatch("orders/initNewOrder", {
          related_terminal: this.terminal.id,
        });

        // add dining options in kiosk mode.
        if (!newOrder.dining_option_type) {
          newOrder.dining_option_type = diningOption.id;
          newOrder.dining_option = diningOption.name;
        }

        this.$store.dispatch("orders/saveOrder", newOrder);
        await this.$router.push({
          name: "OrderV2",
          params: { id: newOrder.local_id, diningOption: diningOption.id },
        });
      } else {
        this.$router.push({ name: "KioskOrderTypeV2" });
      }
    },
    goToTools() {
      this.$router.push({ name: "Tools" });
    },
  },
};
</script>

<style scoped>
.kiosk-home-v2 {
  height: 100vh;
  width: 100%;
  background: linear-gradient(rgba(15, 23, 42, 0.5), rgba(15, 23, 42, 0.8));
  color: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 80px 24px 64px;
  cursor: pointer;
  overflow: hidden;
  background-size: cover;
  background-position: center;
}

.kiosk-home-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: space-between;
}

.kiosk-logo-v2 {
  max-height: clamp(120px, 20vh, 220px);
  max-width: 80%;
  object-fit: contain;
  position: relative;
  z-index: 7;
}

.kiosk-footer-v2 {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: auto;
}

.kiosk-powered-v2 {
  max-height: 40px;
  opacity: 0.8;
  cursor: pointer;
  z-index: 8;
}

.kiosk-welcome-section {
  position: relative;
  z-index: 5;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.kiosk-title {
  font-size: 64px;
  font-weight: 900;
  line-height: 1.2;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  font-family: "Merriweather", serif;
}

.kiosk-subtitle {
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  opacity: 0.9;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.terminal-version-v2 {
  font-size: 14px;
  opacity: 0.8;
  z-index: 7;
}

@media (max-height: 768px) {
  .kiosk-logo-v2 {
    top: 20px;
  }

  .kiosk-title {
    font-size: 48px;
  }

  .kiosk-subtitle {
    font-size: 20px;
  }
}
</style>
